// src/components/SalaryDeductions.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Autocomplete,
  Popper,
  Divider,
  FormHelperText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  AttachMoney as MoneyIcon,
  Comment as CommentIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  RemoveCircle as DeductIcon,
  CalendarMonth as CalendarIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

import Sidebar from '../../../../components/sidebars/Sidebar_HR';
import TopNav from '../../../../components/TopNav_hotelStaff';

// ==================== CONSTANTS ====================
const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

// ==================== HELPER FUNCTIONS ====================

/**
 * Sanitize string input to prevent XSS attacks
 */
const sanitizeString = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .replace(/[<>]/g, '')
    .replace(/[&]/g, '&amp;')
    .replace(/["]/g, '&quot;')
    .replace(/[']/g, '&#x27;')
    .replace(/[/]/g, '&#x2F;');
};

/**
 * Format currency for Nigerian Naira
 */
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '₦0.00';
  const num = parseFloat(amount);
  if (isNaN(num)) return '₦0.00';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

/**
 * Format date safely
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-NG');
  } catch (error) {
    console.error('[SalaryDeductions] Date formatting error:', error);
    return 'N/A';
  }
};

/**
 * Log security events
 */
const logSecurityEvent = (eventType, details) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    eventType,
    details,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };
  console.debug(`[SECURITY] ${eventType}:`, logEntry);
};

/**
 * Get numeric status from deduction
 */
const getNumericStatus = (deduction) => {
  return Number(deduction?.deduction_status);
};

/**
 * Get status text
 */
const getStatusText = (status) => {
  const numStatus = Number(status);
  if (numStatus === 0) return 'Pending';
  if (numStatus === 1) return 'Approved';
  if (numStatus === 2) return 'Rejected';
  return 'Unknown';
};

/**
 * Get status color
 */
const getStatusColor = (status) => {
  const numStatus = Number(status);
  if (numStatus === 0) return '#ff9800';
  if (numStatus === 1) return '#4caf50';
  if (numStatus === 2) return '#f44336';
  return '#757575';
};

// ==================== MAIN COMPONENT ====================
const SalaryDeductions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Data states
  const [deductions, setDeductions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search/filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [selectedDeduction, setSelectedDeduction] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [actionComment, setActionComment] = useState('');
  
  // Employee search states (enhanced)
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  
  // Staff info states
  const [staffInfo, setStaffInfo] = useState(null);
  const [fetchingInfo, setFetchingInfo] = useState(false);
  const [existingDeductions, setExistingDeductions] = useState([]);
  const [periodSummary, setPeriodSummary] = useState(null);
  const [checkingDeductions, setCheckingDeductions] = useState(false);
  
  // Form states
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  const years = Array.from({ length: 3 }, (_, i) => currentYear - 1 + i);
  
  const [createForm, setCreateForm] = useState({
    phone: '',
    amount: '',
    remark: '',
    payroll_month: currentMonth,
    payroll_year: currentYear,
  });
  
  const [amountValidation, setAmountValidation] = useState({ valid: false, message: '', warning: null });
  
  // Refs
  const searchDebounceRef = useRef(null);
  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
    console.debug('[SalaryDeductions] Sidebar toggled:', !sidebarOpen);
  };

  // ==================== API CALLS ====================
  const getApiBaseUrl = () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    console.debug(`[SalaryDeductions] API Base URL: ${baseUrl || 'default'}`);
    return baseUrl;
  };

  // Fetch all deductions
  const fetchDeductions = useCallback(async () => {
    const startTime = performance.now();
    console.debug('[SalaryDeductions] Fetching deductions...');
    
    try {
      setLoading(true);
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/hotel/HR/salary_deductions.php`);
      const result = await response.json();
      
      if (result.success) {
        const processedData = result.data.map(item => ({
          ...item,
          deduction_status: Number(item.deduction_status)
        }));
        setDeductions(processedData);
        setStats(result.stats);
        console.debug(`[SalaryDeductions] Loaded ${processedData.length} deductions in ${(performance.now() - startTime).toFixed(2)}ms`);
      } else {
        setError(result.message);
        console.error('[SalaryDeductions] API Error:', result.message);
      }
    } catch (err) {
      console.error('[SalaryDeductions] Fetch error:', err);
      setError('Failed to load deductions');
      setSnackbar({ open: true, message: 'Failed to load deductions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  // Search employees by name or phone (enhanced - fetches all employees and filters)
  const searchEmployees = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setSearchError('');
    
    try {
      const baseUrl = getApiBaseUrl();
      console.debug(`[SalaryDeductions] Searching employees with term: "${searchTerm}"`);
      
      // Fetch all active employees using update_salary.php?type=all
      const response = await axios.get(`${baseUrl}/hotel/HR/update_salary.php?type=all`);
      
      if (response.data.success && response.data.data) {
        const allEmployees = response.data.data;
        const searchLower = searchTerm.toLowerCase();
        
        const filtered = allEmployees.filter(emp => 
          (emp.full_name || '').toLowerCase().includes(searchLower) ||
          (emp.surname || '').toLowerCase().includes(searchLower) ||
          (emp.other_names || '').toLowerCase().includes(searchLower) ||
          (emp.phone || '').includes(searchTerm) ||
          (emp.email || '').toLowerCase().includes(searchLower)
        );
        
        setSearchResults(filtered);
        console.debug(`[SalaryDeductions] Found ${filtered.length} employees matching "${searchTerm}"`);
        
        if (filtered.length === 0) {
          setSearchError('No employees found matching your search');
        }
      } else {
        setSearchResults([]);
        setSearchError('Failed to load employee list');
      }
    } catch (error) {
      console.error('[SalaryDeductions] Error searching employees:', error);
      setSearchResults([]);
      setSearchError('Error searching for employees');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Fetch complete staff info and existing deductions
  const fetchStaffInfoByPhone = useCallback(async (phone, month = null, year = null) => {
    if (!phone) {
      setStaffInfo(null);
      setExistingDeductions([]);
      setPeriodSummary(null);
      return;
    }

    setFetchingInfo(true);
    setCheckingDeductions(true);
    
    try {
      const baseUrl = getApiBaseUrl();
      const checkMonth = month || createForm.payroll_month;
      const checkYear = year || createForm.payroll_year;
      
      console.debug(`[SalaryDeductions] Fetching staff info for phone: ${phone}, period: ${checkMonth}/${checkYear}`);
      
      // Get staff basic info - FIXED: Use salary_deductions.php with type=staff_info
      const staffResponse = await axios.get(
        `${baseUrl}/hotel/HR/salary_deductions.php?type=staff_info&phone=${encodeURIComponent(phone)}`
      );
      
      if (staffResponse.data.success && staffResponse.data.data) {
        // Extract employee data - note the field names from PHP response
        const employeeData = staffResponse.data.data.employee || staffResponse.data.data;
        
        // IMPORTANT: The PHP returns 'current_salary', not 'salary_amount'
        const salaryAmount = employeeData.current_salary || employeeData.salary_amount || 0;
        
        const processedStaffInfo = {
          ...employeeData,
          salary_amount: salaryAmount,  // Add salary_amount for compatibility
          current_salary: salaryAmount,
          full_name: employeeData.full_name || `${employeeData.first_name || ''} ${employeeData.other_names || ''}`.trim() || 'Unknown'
        };
        
        setStaffInfo(processedStaffInfo);
        console.debug('[SalaryDeductions] Staff info loaded:', processedStaffInfo);
        
        // Get existing deductions for the selected month
        if (employeeData.employee_id || employeeData.id) {
          const employeeId = employeeData.employee_id || employeeData.id;
          const deductionsResponse = await axios.get(
            `${baseUrl}/hotel/HR/salary_deductions.php?employee_id=${employeeId}&month=${checkMonth}&year=${checkYear}&type=summary`
          );
          
          if (deductionsResponse.data.success) {
            const processedDeductions = (deductionsResponse.data.data?.all_deductions || []).map(item => ({
              ...item,
              deduction_status: Number(item.deduction_status)
            }));
            setExistingDeductions(processedDeductions);
            setPeriodSummary(deductionsResponse.data.data);
            console.debug(`[SalaryDeductions] Found ${processedDeductions.length} existing deductions for period`);
          }
        }
      } else {
        console.warn('[SalaryDeductions] No staff info found for phone:', phone);
        setStaffInfo(null);
        setExistingDeductions([]);
        setPeriodSummary(null);
        setSearchError('Employee not found or inactive');
      }
    } catch (error) {
      console.error('[SalaryDeductions] Error fetching staff info:', error);
      setStaffInfo(null);
      setExistingDeductions([]);
      setPeriodSummary(null);
      if (error.response?.status === 404) {
        setSearchError('Employee not found or inactive');
      }
    } finally {
      setFetchingInfo(false);
      setCheckingDeductions(false);
    }
  }, [createForm.payroll_month, createForm.payroll_year]);

  // ==================== VALIDATION ====================
  const getRemainingAvailable = useCallback(() => {
    // Priority 1: Use periodSummary which has accurate remaining calculation
    if (periodSummary && periodSummary.remaining_available !== undefined) {
      const remaining = parseFloat(periodSummary.remaining_available);
      console.debug('[SalaryDeductions] getRemainingAvailable from periodSummary:', remaining);
      return isNaN(remaining) ? 0 : remaining;
    }
    
    // Priority 2: Use periodSummary salary and total deductions
    if (periodSummary && periodSummary.salary !== undefined) {
      const salary = parseFloat(periodSummary.salary);
      const totalDeductions = parseFloat(periodSummary.total_pending_deductions || 0);
      const remaining = salary - totalDeductions;
      console.debug('[SalaryDeductions] getRemainingAvailable calculated from periodSummary:', remaining);
      return isNaN(remaining) ? 0 : Math.max(0, remaining);
    }
    
    // Priority 3: Use staffInfo salary_amount
    if (staffInfo) {
      const salary = parseFloat(staffInfo.salary_amount || staffInfo.current_salary || 0);
      console.debug('[SalaryDeductions] getRemainingAvailable from staffInfo salary:', salary);
      return isNaN(salary) ? 0 : salary;
    }
    
    console.debug('[SalaryDeductions] getRemainingAvailable: no salary info available');
    return 0;
  }, [periodSummary, staffInfo]);

  const validateAmount = useCallback((amount, salary) => {
    const numAmount = parseFloat(amount);
    const numSalary = parseFloat(salary);
    
    if (isNaN(numAmount) || numAmount === 0) {
      return { valid: false, message: 'Please enter a valid amount', warning: null };
    }
    if (numAmount <= 0) {
      return { valid: false, message: 'Amount must be greater than 0', warning: null };
    }
    if (numSalary > 0 && numAmount > numSalary) {
      return { valid: false, message: `Amount cannot exceed salary (${formatCurrency(numSalary)})`, warning: null };
    }
    if (numSalary > 0 && numAmount > numSalary * 0.5) {
      return { valid: true, warning: `This deduction exceeds 50% of the employee's salary. Please review.` };
    }
    
    return { valid: true, message: 'Valid amount', warning: null };
  }, []);

  const canAddDeduction = useCallback(() => {
    const amount = parseFloat(createForm.amount);
    if (isNaN(amount) || amount <= 0) {
      console.debug('[SalaryDeductions] canAddDeduction: false - invalid amount');
      return false;
    }
    
    if (!createForm.remark || createForm.remark.trim().length < 3) {
      console.debug('[SalaryDeductions] canAddDeduction: false - remark too short');
      return false;
    }
    
    if (!selectedEmployee) {
      console.debug('[SalaryDeductions] canAddDeduction: false - no employee selected');
      return false;
    }
    
    const remaining = getRemainingAvailable();
    const result = amount <= remaining;
    console.debug(`[SalaryDeductions] canAddDeduction: amount=${amount}, remaining=${remaining}, result=${result}`);
    return result;
  }, [createForm.amount, createForm.remark, selectedEmployee, getRemainingAvailable]);

  // Update validation when amount changes
  useEffect(() => {
    if (createForm.amount && staffInfo) {
      const salary = staffInfo.current_salary || staffInfo.salary_amount || 0;
      const validation = validateAmount(createForm.amount, salary);
      setAmountValidation(validation);
    } else {
      setAmountValidation({ valid: false, message: '', warning: null });
    }
  }, [createForm.amount, staffInfo, validateAmount]);

  // Debug log when canAddDeduction changes
  useEffect(() => {
    console.debug('[SalaryDeductions] canAddDeduction status:', canAddDeduction(), {
      amount: createForm.amount,
      remarkLength: createForm.remark?.length,
      hasEmployee: !!selectedEmployee,
      remaining: getRemainingAvailable()
    });
  }, [canAddDeduction, createForm.amount, createForm.remark, selectedEmployee, getRemainingAvailable]);

  // ==================== HANDLER FUNCTIONS ====================
  
  // Handle employee selection from autocomplete
  const handleEmployeeSelect = (event, employee) => {
    if (employee) {
      console.debug(`[SalaryDeductions] Employee selected: ${employee.full_name || employee.surname} ${employee.other_names || ''} (${employee.phone})`);
      setSelectedEmployee(employee);
      setSearchInput(`${employee.full_name || employee.surname} ${employee.other_names || ''} (${employee.phone})`);
      setSearchResults([]);
      setSearchError('');
      setCreateForm(prev => ({ ...prev, phone: employee.phone }));
      fetchStaffInfoByPhone(employee.phone, createForm.payroll_month, createForm.payroll_year);
    } else {
      handleClearSelection();
    }
  };

  const handleClearSelection = () => {
    console.debug('[SalaryDeductions] Clearing employee selection');
    setSelectedEmployee(null);
    setSearchInput('');
    setStaffInfo(null);
    setExistingDeductions([]);
    setPeriodSummary(null);
    setCreateForm(prev => ({ ...prev, phone: '', amount: '', remark: '' }));
    setAmountValidation({ valid: false, message: '', warning: null });
    setSearchResults([]);
    setSearchError('');
  };

  const handleMonthYearChange = async (field, value) => {
    const updatedForm = { ...createForm, [field]: value };
    setCreateForm(updatedForm);
    console.debug(`[SalaryDeductions] ${field} changed to:`, value);
    
    if (staffInfo && staffInfo.phone) {
      fetchStaffInfoByPhone(staffInfo.phone, updatedForm.payroll_month, updatedForm.payroll_year);
    } else if (selectedEmployee && selectedEmployee.phone) {
      fetchStaffInfoByPhone(selectedEmployee.phone, updatedForm.payroll_month, updatedForm.payroll_year);
    }
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^0-9.]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }
    setCreateForm(prev => ({ ...prev, amount: value }));
  };

  const handleCreateDeduction = async () => {
    logSecurityEvent('DEDUCTION_CREATE_ATTEMPT', { 
      phone: createForm.phone, 
      amount: createForm.amount,
      month: createForm.payroll_month,
      year: createForm.payroll_year
    });
    
    if (!selectedEmployee) {
      setSnackbar({ open: true, message: 'Please select a valid employee from search', severity: 'error' });
      return;
    }

    const amount = parseFloat(createForm.amount);
    if (isNaN(amount) || amount <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid positive amount', severity: 'error' });
      return;
    }

    if (!createForm.payroll_month || createForm.payroll_month < 1 || createForm.payroll_month > 12) {
      setSnackbar({ open: true, message: 'Please select a valid month', severity: 'error' });
      return;
    }

    if (!createForm.payroll_year || createForm.payroll_year < 2000) {
      setSnackbar({ open: true, message: 'Please select a valid year', severity: 'error' });
      return;
    }

    if (!createForm.remark || createForm.remark.trim().length < 3) {
      setSnackbar({ open: true, message: 'Please provide a detailed reason (minimum 3 characters)', severity: 'error' });
      return;
    }

    const remaining = getRemainingAvailable();
    if (amount > remaining) {
      setSnackbar({ 
        open: true, 
        message: `This deduction (${formatCurrency(amount)}) exceeds the remaining available amount (${formatCurrency(remaining)}) for this staff in ${MONTHS.find(m => m.value === createForm.payroll_month)?.label} ${createForm.payroll_year}.`, 
        severity: 'error' 
      });
      return;
    }

    setLoading(true);
    const startTime = performance.now();

    try {
      const baseUrl = getApiBaseUrl();
      
      let requestedBy = 'HR';
      try {
        const userProfile = localStorage.getItem('userProfile');
        if (userProfile) {
          const profile = JSON.parse(userProfile);
          requestedBy = profile.name || profile.full_name || 'HR';
        }
      } catch (e) {
        console.error('[SalaryDeductions] Error parsing user profile:', e);
      }
      
      const deductionData = {
        phone: createForm.phone,
        amount: amount,
        remark: sanitizeString(createForm.remark),
        payroll_month: parseInt(createForm.payroll_month),
        payroll_year: parseInt(createForm.payroll_year),
        requested_by: requestedBy
      };
      
      console.debug('[SalaryDeductions] Creating deduction:', deductionData);
      
      const response = await axios.post(`${baseUrl}/hotel/HR/salary_deductions.php`, deductionData);
      
      if (response.data.success) {
        console.debug(`[SalaryDeductions] Deduction created in ${(performance.now() - startTime).toFixed(2)}ms`);
        setSnackbar({ open: true, message: response.data.message, severity: 'success' });
        setCreateDialogOpen(false);
        handleClearSelection();
        setCreateForm({ 
          phone: '', 
          amount: '', 
          remark: '',
          payroll_month: currentMonth,
          payroll_year: currentYear
        });
        fetchDeductions();
        logSecurityEvent('DEDUCTION_CREATE_SUCCESS', { deduction_id: response.data.deduction_id });
      } else {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
        logSecurityEvent('DEDUCTION_CREATE_FAILED', { error: response.data.message });
      }
    } catch (err) {
      console.error('[SalaryDeductions] Error creating deduction:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to create deduction', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedDeduction) return;
    
    logSecurityEvent('DEDUCTION_APPROVE_ATTEMPT', { id: selectedDeduction.id });
    
    setLoading(true);
    const startTime = performance.now();

    try {
      const baseUrl = getApiBaseUrl();
      
      let approvedBy = 'HR Manager';
      try {
        const userProfile = localStorage.getItem('userProfile');
        if (userProfile) {
          const profile = JSON.parse(userProfile);
          approvedBy = profile.name || profile.full_name || 'HR Manager';
        }
      } catch (e) {
        console.error('[SalaryDeductions] Error parsing user profile:', e);
      }
      
      const response = await fetch(`${baseUrl}/hotel/HR/salary_deductions.php`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: selectedDeduction.id,
          action: 'approve',
          approved_by: approvedBy,
          rejection_reason: null
        }),
      });
      const result = await response.json();
      
      if (result.success) {
        console.debug(`[SalaryDeductions] Deduction approved in ${(performance.now() - startTime).toFixed(2)}ms`);
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setApproveDialogOpen(false);
        setActionComment('');
        fetchDeductions();
        logSecurityEvent('DEDUCTION_APPROVE_SUCCESS', { id: selectedDeduction.id });
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      console.error('[SalaryDeductions] Error approving deduction:', err);
      setSnackbar({ open: true, message: 'Failed to approve deduction', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedDeduction) return;
    
    if (!actionComment || actionComment.trim().length < 3) {
      setSnackbar({ open: true, message: 'Please provide a detailed reason for rejection (minimum 3 characters)', severity: 'error' });
      return;
    }

    logSecurityEvent('DEDUCTION_REJECT_ATTEMPT', { id: selectedDeduction.id, reason: actionComment });
    
    setLoading(true);
    const startTime = performance.now();

    try {
      const baseUrl = getApiBaseUrl();
      
      let approvedBy = 'HR Manager';
      try {
        const userProfile = localStorage.getItem('userProfile');
        if (userProfile) {
          const profile = JSON.parse(userProfile);
          approvedBy = profile.name || profile.full_name || 'HR Manager';
        }
      } catch (e) {
        console.error('[SalaryDeductions] Error parsing user profile:', e);
      }
      
      const response = await fetch(`${baseUrl}/hotel/HR/salary_deductions.php`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: selectedDeduction.id,
          action: 'reject',
          approved_by: approvedBy,
          rejection_reason: sanitizeString(actionComment)
        }),
      });
      const result = await response.json();
      
      if (result.success) {
        console.debug(`[SalaryDeductions] Deduction rejected in ${(performance.now() - startTime).toFixed(2)}ms`);
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setRejectDialogOpen(false);
        setActionComment('');
        fetchDeductions();
        logSecurityEvent('DEDUCTION_REJECT_SUCCESS', { id: selectedDeduction.id });
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      console.error('[SalaryDeductions] Error rejecting deduction:', err);
      setSnackbar({ open: true, message: 'Failed to reject deduction', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDeduction) return;
    
    logSecurityEvent('DEDUCTION_DELETE_ATTEMPT', { id: selectedDeduction.id });
    
    setLoading(true);
    const startTime = performance.now();

    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/hotel/HR/salary_deductions.php?id=${selectedDeduction.id}`, {
        method: 'DELETE',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const result = await response.json();
      
      if (result.success) {
        console.debug(`[SalaryDeductions] Deduction deleted in ${(performance.now() - startTime).toFixed(2)}ms`);
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setDeleteDialogOpen(false);
        fetchDeductions();
        logSecurityEvent('DEDUCTION_DELETE_SUCCESS', { id: selectedDeduction.id });
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      console.error('[SalaryDeductions] Error deleting deduction:', err);
      setSnackbar({ open: true, message: 'Failed to delete deduction', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ==================== DERIVED VALUES ====================
  const filteredDeductions = useMemo(() => {
    let filtered = [...deductions];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(deduction => 
        (deduction.full_name || '').toLowerCase().includes(term) ||
        (deduction.department || '').toLowerCase().includes(term) ||
        (deduction.phone || '').includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      const statusNum = statusFilter === 'pending' ? 0 : (statusFilter === 'approved' ? 1 : 2);
      filtered = filtered.filter(deduction => getNumericStatus(deduction) === statusNum);
    }
    
    return filtered;
  }, [deductions, searchTerm, statusFilter]);

  const pendingCount = stats?.pending || 0;
  const approvedCount = stats?.approved || 0;
  const totalPendingAmount = stats?.total_pending_amount || 0;
  const totalApprovedAmount = stats?.total_approved_amount || 0;

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    fetchDeductions();
  }, [fetchDeductions]);

  // Debounced search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    if (searchInput.trim().length >= 2 && !selectedEmployee) {
      searchDebounceRef.current = setTimeout(() => {
        searchEmployees(searchInput);
      }, 400);
    } else if (searchInput.trim().length < 2) {
      setSearchResults([]);
      setSearchError('');
    }
    
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInput, selectedEmployee, searchEmployees]);

  // ==================== COMPONENT RENDER ====================
  const StatCard = ({ title, value, subtitle, color, icon: Icon }) => (
    <Card sx={{ 
      borderRadius: 3, 
      height: '100%', 
      boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>{title}</Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ color: color }}>{value}</Typography>
            {subtitle && <Typography variant="caption" color="textSecondary">{subtitle}</Typography>}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  // Custom popper for autocomplete
  const CustomPopper = (props) => (
    <Popper {...props} placement="bottom-start" style={{ width: props.style?.width, zIndex: 1300 }}>
      <Paper elevation={4} sx={{ mt: 1, maxHeight: 400, overflow: 'auto' }}>
        {searchLoading && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <CircularProgress size={30} />
            <Typography variant="body2" sx={{ mt: 1 }}>Searching...</Typography>
          </Box>
        )}
        {!searchLoading && searchError && !searchResults.length && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="error">{searchError}</Typography>
          </Box>
        )}
        {!searchLoading && !searchError && searchResults.length === 0 && searchInput.length >= 2 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">No employees found</Typography>
          </Box>
        )}
        {props.children}
      </Paper>
    </Popper>
  );

  if (loading && deductions.length === 0) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading deductions...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box sx={{ 
        flexGrow: 1, 
        p: 3, 
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5, 
        maxWidth: '1400px', 
        mx: 'auto', 
        width: '100%', 
        overflow: 'auto', 
        height: '100vh' 
      }}>
        <TopNav
          onLogout={() => {
            logSecurityEvent('USER_LOGOUT', {});
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => console.debug('[SalaryDeductions] Notification clicked')}
          toggleSidebar={toggleSidebar}
        />

     
        {/* Header */}
        <Paper elevation={0} sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3, 
          background: `linear-gradient(135deg, #C84646 0%, #a33a3a 100%)`, 
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                Salary Deductions
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5, fontFamily: 'Times New Roman, serif' }}>
                Manage staff salary deductions (offences, penalties, etc.)
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5, display: 'block' }}>
                Note: Multiple deductions allowed per month, but total cannot exceed staff's salary
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="contained" 
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />} 
                onClick={fetchDeductions}
                disabled={loading}
                sx={{ bgcolor: 'white', color: '#C84646', '&:hover': { bgcolor: '#f5f5f5' }, textTransform: 'none' }}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={() => {
                  handleClearSelection();
                  setCreateDialogOpen(true);
                }} 
                sx={{ bgcolor: 'white', color: '#C84646', '&:hover': { bgcolor: '#f5f5f5' }, textTransform: 'none' }}
              >
                New Deduction
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Pending Approvals" value={pendingCount} color="#ff9800" icon={PendingIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Approved" value={approvedCount} color="#4caf50" icon={CheckCircleIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Pending Amount" value={formatCurrency(totalPendingAmount)} color="#ff9800" icon={MoneyIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Approved Amount" value={formatCurrency(totalApprovedAmount)} color="#4caf50" icon={MoneyIcon} />
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by name, department, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 2, minWidth: 200 }}
              InputProps={{ 
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
              inputProps={{ style: { fontFamily: 'Times New Roman, serif' } }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontFamily: 'Times New Roman, serif' }}>Status</InputLabel>
              <Select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)} 
                label="Status"
                sx={{ fontFamily: 'Times New Roman, serif' }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />} 
              onClick={fetchDeductions}
              sx={{ fontFamily: 'Times New Roman, serif', textTransform: 'none' }}
            >
              Refresh
            </Button>
          </Box>
        </Paper>

        {/* Deductions Table - EXPANDED HEIGHT */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 2 }}>
          <TableContainer sx={{ 
            maxHeight: 'calc(100vh - 420px)',
            minHeight: '450px',
            overflow: 'auto',
            '& .MuiTable-root': { minWidth: 1100 }
          }}>
            <Table stickyHeader size="medium">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Staff Member</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Department</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Payroll Month</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Remark</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Date Created</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 2, fontFamily: 'Times New Roman, serif' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDeductions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                        No deduction records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDeductions.map((deduction) => {
                    const statusColor = getStatusColor(deduction.deduction_status);
                    const statusText = getStatusText(deduction.deduction_status);
                    const numericStatus = getNumericStatus(deduction);
                    
                    return (
                      <TableRow key={deduction.id} hover sx={{ height: '72px' }}>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: statusColor, width: 40, height: 40 }}>
                              <PersonIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'Times New Roman, serif' }}>
                                {sanitizeString(deduction.full_name) || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontFamily: 'Times New Roman, serif' }}>
                                {deduction.phone}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <Chip size="small" label={sanitizeString(deduction.department) || 'N/A'} variant="outlined" />
                        </TableCell>
                        <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                          <Typography variant="body2" fontWeight="bold" color="#f44336" sx={{ fontFamily: 'monospace' }}>
                            -{formatCurrency(deduction.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          {deduction.payroll_month && deduction.payroll_year ? (
                            <Chip 
                              size="small" 
                              label={`${MONTHS.find(m => m.value === deduction.payroll_month)?.label || deduction.payroll_month} ${deduction.payroll_year}`}
                              variant="outlined"
                              icon={<CalendarIcon sx={{ fontSize: 14 }} />}
                            />
                          ) : (
                            <Chip size="small" label="Not set" variant="outlined" color="warning" />
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <Tooltip title={deduction.remark || 'No remark'}>
                            <Chip 
                              size="small" 
                              label={(deduction.remark || 'No remark').substring(0, 30)} 
                              variant="outlined" 
                              icon={<CommentIcon sx={{ fontSize: 14 }} />} 
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <Chip 
                            label={statusText} 
                            size="small"
                            sx={{ bgcolor: alpha(statusColor, 0.1), color: statusColor, fontWeight: 'medium' }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {formatDate(deduction.date_created)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                          {numericStatus === 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="Delete">
                                <IconButton 
                                  size="small" 
                                  onClick={() => { 
                                    console.debug(`[SalaryDeductions] Opening delete dialog for deduction ${deduction.id}`);
                                    setSelectedDeduction(deduction); 
                                    setDeleteDialogOpen(true); 
                                  }} 
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {filteredDeductions.length > 0 && (
            <Box sx={{ 
              p: 2, 
              bgcolor: '#fafafa', 
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2
            }}>
              <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
                <strong>Showing:</strong> {filteredDeductions.length} of {deductions.length} deductions
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography variant="body2" sx={{ color: '#ff9800', fontFamily: 'Times New Roman, serif' }}>
                  <strong>Pending Total:</strong> {formatCurrency(totalPendingAmount)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#4caf50', fontFamily: 'Times New Roman, serif' }}>
                  <strong>Approved Total:</strong> {formatCurrency(totalApprovedAmount)}
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>

        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
            Designed by Elegantè IT - Salary Deductions Management System
          </Typography>
        </footer>
      </Box>

      {/* Create Deduction Dialog - WITH ENHANCED SEARCH */}
      <Dialog open={createDialogOpen} onClose={() => {
        handleClearSelection();
        setCreateDialogOpen(false);
      }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeductIcon /> Add Salary Deduction
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#f44336' }}>
              Search Employee (by Name or Phone)
            </Typography>
            
            {/* Enhanced Employee Search with Autocomplete */}
            <Autocomplete
              freeSolo
              options={searchResults}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return `${option.surname || ''} ${option.other_names || ''} (${option.phone || ''})`;
              }}
              value={selectedEmployee}
              onChange={handleEmployeeSelect}
              inputValue={searchInput}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  setSearchInput(newInputValue);
                  if (!newInputValue) {
                    handleClearSelection();
                  }
                }
              }}
              loading={searchLoading}
              PopperComponent={CustomPopper}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type employee name or phone number (min. 2 characters)..."
                  variant="outlined"
                  error={!!searchError}
                  helperText={searchError || "Search by name (e.g., John, Nicholas) or phone number"}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#C84646' }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {searchLoading && <CircularProgress size={20} />}
                        {selectedEmployee && (
                          <IconButton size="small" onClick={handleClearSelection} sx={{ mr: 1 }}>
                            <ClearIcon />
                          </IconButton>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const hasSalary = (option.current_salary || option.salary_amount || 0) > 0;
                const salaryValue = option.current_salary || option.salary_amount || 0;
                return (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', py: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: hasSalary ? '#4caf50' : '#ff9800', width: 40, height: 40 }}>
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {option.surname || ''} {option.other_names || ''}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip size="small" icon={<BusinessIcon sx={{ fontSize: 14 }} />} label={option.department || 'No Dept'} variant="outlined" />
                            <Chip size="small" icon={<PhoneIcon sx={{ fontSize: 14 }} />} label={option.phone} variant="outlined" />
                            <Chip 
                              size="small" 
                              label={formatCurrency(salaryValue)} 
                              sx={{ bgcolor: hasSalary ? '#e8f5e9' : '#fff3e0', color: hasSalary ? '#2e7d32' : '#ed6c02' }}
                            />
                          </Box>
                        </Box>
                      </Box>
                      <Chip size="small" label={option.status === 'Active' ? 'Active' : 'Inactive'} color={option.status === 'Active' ? 'success' : 'default'} />
                    </Box>
                  </li>
                );
              }}
            />
            
            {fetchingInfo && <LinearProgress sx={{ mt: 1 }} />}
            
            {staffInfo && !fetchingInfo && selectedEmployee && (
              <>
                <Alert severity="info" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  <strong>{staffInfo.full_name || staffInfo.surname} {staffInfo.other_names || ''}</strong><br />
                  Department: {staffInfo.department || staffInfo.department_name || 'Not Assigned'}<br />
                  Current Salary: {formatCurrency(staffInfo.current_salary || staffInfo.salary_amount || 0)}
                </Alert>
                
                {/* Month/Year Selection */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ fontFamily: 'Times New Roman, serif' }}>Payroll Month *</InputLabel>
                      <Select
                        value={createForm.payroll_month}
                        onChange={(e) => handleMonthYearChange('payroll_month', e.target.value)}
                        label="Payroll Month *"
                        sx={{ fontFamily: 'Times New Roman, serif' }}
                      >
                        {MONTHS.map(month => (
                          <MenuItem key={month.value} value={month.value} sx={{ fontFamily: 'Times New Roman, serif' }}>
                            {month.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ fontFamily: 'Times New Roman, serif' }}>Payroll Year *</InputLabel>
                      <Select
                        value={createForm.payroll_year}
                        onChange={(e) => handleMonthYearChange('payroll_year', e.target.value)}
                        label="Payroll Year *"
                        sx={{ fontFamily: 'Times New Roman, serif' }}
                      >
                        {years.map(year => (
                          <MenuItem key={year} value={year} sx={{ fontFamily: 'Times New Roman, serif' }}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                
                {/* Existing Deductions Display */}
                {checkingDeductions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  existingDeductions.filter(d => getNumericStatus(d) === 0).length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, fontFamily: 'Times New Roman, serif' }}>
                        Existing Pending Deductions for {MONTHS.find(m => m.value === createForm.payroll_month)?.label} {createForm.payroll_year}:
                      </Typography>
                      <List dense disablePadding>
                        {existingDeductions.filter(d => getNumericStatus(d) === 0).map((ded, idx) => (
                          <ListItem key={ded.id} disablePadding sx={{ pl: 2 }}>
                            <ListItemText 
                              primary={`#${idx + 1}: ${formatCurrency(ded.amount)} - ${ded.remark}`}
                              secondary={`Status: ${getStatusText(ded.deduction_status)}`}
                              primaryTypographyProps={{ variant: 'body2', fontFamily: 'Times New Roman, serif' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                      {periodSummary && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
                            <strong>Total Deductions:</strong> {formatCurrency(periodSummary.total_pending_deductions || periodSummary.total_deductions)}<br />
                            <strong>Salary:</strong> {formatCurrency(periodSummary.salary)}<br />
                            <strong>Remaining Available:</strong> {formatCurrency(periodSummary.remaining_available)}
                          </Typography>
                          {periodSummary.remaining_available <= 0 && (
                            <Typography variant="body2" color="error" sx={{ mt: 1, fontFamily: 'Times New Roman, serif' }}>
                              <WarningIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                              No more deductions can be added - salary limit reached!
                            </Typography>
                          )}
                          {periodSummary.remaining_available > 0 && periodSummary.salary > 0 && (
                            <LinearProgress 
                              variant="determinate" 
                              value={(periodSummary.total_pending_deductions / periodSummary.salary) * 100} 
                              sx={{ mt: 1, height: 8, borderRadius: 4 }}
                              color="warning"
                            />
                          )}
                        </Box>
                      )}
                    </Alert>
                  )
                )}
                
                <TextField
                  label="Deduction Amount (₦)"
                  type="text"
                  placeholder="Enter amount to deduct (e.g., 50000)"
                  fullWidth
                  value={createForm.amount}
                  onChange={handleAmountChange}
                  error={!!amountValidation.message && !amountValidation.valid}
                  helperText={amountValidation.message || (amountValidation.warning ? amountValidation.warning : 
                    (createForm.amount && `Remaining after this deduction: ${formatCurrency(getRemainingAvailable() - parseFloat(createForm.amount || 0))}`))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><MoneyIcon sx={{ color: '#C84646' }} /></InputAdornment>,
                  }}
                  sx={{ fontFamily: 'Times New Roman, serif' }}
                />
                
                {amountValidation.warning && !amountValidation.message && (
                  <FormHelperText error={false} sx={{ color: '#ff9800' }}>
                    ⚠️ {amountValidation.warning}
                  </FormHelperText>
                )}
                
                <TextField
                  label="Reason/Remark *"
                  multiline
                  rows={3}
                  placeholder="e.g., Late coming, Damaged property, etc. (minimum 3 characters)"
                  fullWidth
                  required
                  value={createForm.remark}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, remark: e.target.value }))}
                  helperText="Please provide a clear and detailed reason for this deduction"
                  inputProps={{ minLength: 3 }}
                  sx={{ fontFamily: 'Times New Roman, serif' }}
                />
                
                <Alert severity="warning" sx={{ mt: 1, fontFamily: 'Times New Roman, serif' }}>
                  <strong>Important:</strong> This deduction will only apply to the selected payroll month ({MONTHS.find(m => m.value === createForm.payroll_month)?.label} {createForm.payroll_year}). 
                  Multiple deductions are allowed per month, but total deductions cannot exceed the staff's salary.
                </Alert>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            handleClearSelection();
            setCreateDialogOpen(false);
          }} sx={{ fontFamily: 'Times New Roman, serif' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateDeduction} 
            variant="contained" 
            sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#d32f2f' }, fontFamily: 'Times New Roman, serif' }} 
            disabled={!selectedEmployee || !createForm.amount || !createForm.remark || createForm.remark.length < 3 || !canAddDeduction() || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Add Deduction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4caf50', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon /> Approve Salary Deduction
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {selectedDeduction && (
              <Alert severity="info" sx={{ fontFamily: 'Times New Roman, serif' }}>
                <strong>Staff:</strong> {sanitizeString(selectedDeduction.full_name)}<br />
                <strong>Amount:</strong> {formatCurrency(selectedDeduction.amount)}<br />
                <strong>Payroll Period:</strong> {selectedDeduction.payroll_month && selectedDeduction.payroll_year ? 
                  `${MONTHS.find(m => m.value === selectedDeduction.payroll_month)?.label} ${selectedDeduction.payroll_year}` : 'Not specified'}<br />
                <strong>Reason:</strong> {selectedDeduction.remark || 'No reason provided'}
              </Alert>
            )}
            <Alert severity="warning" sx={{ fontFamily: 'Times New Roman, serif' }}>
              <strong>Important:</strong> This amount will be deducted from the staff's salary for the specified payroll month only.
              Please ensure total deductions for this period do not exceed the staff's salary.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} sx={{ fontFamily: 'Times New Roman, serif' }}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" sx={{ bgcolor: '#4caf50', fontFamily: 'Times New Roman, serif' }} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon /> Reject Deduction
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {selectedDeduction && (
              <Alert severity="warning" sx={{ fontFamily: 'Times New Roman, serif' }}>
                <strong>Staff:</strong> {sanitizeString(selectedDeduction.full_name)}<br />
                <strong>Amount:</strong> {formatCurrency(selectedDeduction.amount)}<br />
                <strong>Payroll Period:</strong> {selectedDeduction.payroll_month && selectedDeduction.payroll_year ? 
                  `${MONTHS.find(m => m.value === selectedDeduction.payroll_month)?.label} ${selectedDeduction.payroll_year}` : 'Not specified'}
              </Alert>
            )}
            <TextField
              label="Rejection Reason"
              multiline
              rows={3}
              fullWidth
              required
              value={actionComment}
              onChange={(e) => setActionComment(e.target.value)}
              placeholder="Please provide a detailed reason for rejection (minimum 3 characters)..."
              error={actionComment.length > 0 && actionComment.length < 3}
              helperText={actionComment.length > 0 && actionComment.length < 3 ? 'Please provide at least 3 characters' : ''}
              inputProps={{ minLength: 3 }}
              sx={{ fontFamily: 'Times New Roman, serif' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} sx={{ fontFamily: 'Times New Roman, serif' }}>Cancel</Button>
          <Button 
            onClick={handleReject} 
            variant="contained" 
            sx={{ bgcolor: '#f44336', fontFamily: 'Times New Roman, serif' }} 
            disabled={loading || !actionComment || actionComment.length < 3}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ fontFamily: 'Times New Roman, serif' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Times New Roman, serif' }}>
            Are you sure you want to delete this deduction for <strong>{sanitizeString(selectedDeduction?.full_name)}</strong>?
          </Typography>
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, fontFamily: 'Times New Roman, serif' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontFamily: 'Times New Roman, serif' }}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" sx={{ fontFamily: 'Times New Roman, serif' }} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ fontFamily: 'Times New Roman, serif' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SalaryDeductions;